import { ChevronDownIcon } from '@radix-ui/react-icons';
import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import uniqolor from 'uniqolor';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { barColors, black, white } from '@/components/ui/colors';
import { useReportData } from '@/hooks/useReportData';
import { toTimeHistoryReportChartDataSource } from '@/lib/mappers/timeHistoryReport';
import { cn } from '@/lib/utils';
import { TimeHistoryReportChartDataSource } from '@/types/TimeHistoryReportChartDataSource';
import { TimeHistoryReportFormValues } from '@/types/TimeHistoryReportFormValues';
import { calculateContrastRatio } from '@/utility/colorUtils';

import { Accordion, AccordionContent, AccordionItem } from '../ui/accordion';
import { Button } from '../ui/button';

type AccordionExpansionState = 'open' | 'closed';

interface CustomLabelProps {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: string | number;
  fill?: string;
}

const chartConfig = {
  label: {
    color: 'hsl(var(--list-foreground))',
  },
} satisfies ChartConfig;

const getTooltipLabel = (label: string) => {
  if (label.includes('-Total')) {
    const labelArray = label.split('-Total');
    return labelArray[0];
  } else {
    const labelArray = label.split('.');
    return labelArray[1];
  }
};

const largestToSmallest = ([, valueA]: [string, number], [, valueB]: [string, number]) =>
  valueB - valueA;

const sortProjectTasks = (tasks: object) => {
  const sortedTasks = Object.entries(tasks).sort(largestToSmallest);
  return sortedTasks;
};

const isProjectTaskData = (value: unknown): value is { [key: string]: number } => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((v) => typeof v === 'number')
  );
};

const ChartAccordionWrapper = ({
  children,
  expansionState,
  setExpansionState,
}: {
  children: ReactNode;
  expansionState: AccordionExpansionState;
  setExpansionState: Dispatch<SetStateAction<AccordionExpansionState>>;
}) => {
  const onAccordionValueChange = (value: AccordionExpansionState) => {
    setExpansionState(value);
  };

  return (
    <Accordion
      type="single"
      collapsible
      className="flex h-full w-full flex-col"
      defaultValue="open"
      value={expansionState}
      onValueChange={(value: AccordionExpansionState) => onAccordionValueChange(value)}
    >
      <AccordionItem value="open" className="flex h-full flex-col border-b-0">
        <AccordionContent
          className={cn('h-full pb-0 pt-0', expansionState === 'open' ? 'flex' : 'hidden')}
          forceMount={true}
        >
          {children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const TimeHistoryBarChart = ({
  filterData,
  expansionState,
  chartData,
}: {
  filterData: TimeHistoryReportFormValues;
  expansionState: AccordionExpansionState;
  chartData?: TimeHistoryReportChartDataSource;
}) => {
  const [colorMap, setColorMap] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!chartData) {
      return;
    }

    let currentColor = -1;
    const tempColorMapArray = chartData.map((bar) => {
      const projectData = bar[bar.Project];
      const sortedProjectTasks = sortProjectTasks(
        isProjectTaskData(projectData) ? projectData : {}
      );

      if (sortedProjectTasks.length === 0) {
        currentColor += 1;
        return [{ [bar.Project]: currentColor }];
      } else {
        return sortedProjectTasks.map(([key]: [string, number]) => {
          currentColor += 1;
          return { [`${bar.Project}.${key}`]: currentColor };
        });
      }
    });

    const tempColorMapObject = {};
    tempColorMapArray.forEach((map) => {
      Object.assign(tempColorMapObject, ...map);
    });

    if (Object.entries(colorMap).length === 0) {
      setColorMap(tempColorMapObject);
    }

    if (Object.entries(tempColorMapObject).length >= Object.entries(colorMap).length) {
      setColorMap(tempColorMapObject);
    }
    // TODO: Is there a way to accomplish this while still abiding by the exaustive deps rule?
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData.startDate, filterData.endDate]);

  const largest =
    (chartData?.reduce((max, entry) => Math.max(max, Number(entry.Total)), 0) ?? 0) * 1.05;

  // Custom label component that only shows text if the bar is wide enough
  const CustomLabel = ({ x, y, width, height, value, fill }: CustomLabelProps) => {
    if (
      typeof x !== 'number' ||
      typeof y !== 'number' ||
      typeof width !== 'number' ||
      typeof height !== 'number' ||
      typeof value !== 'number'
    )
      return null;

    if (width < 20) {
      return null;
    }

    return (
      <text
        x={x + width / 2}
        y={y + height / 1.8}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={12}
        fill={fill}
      >
        {value}
      </text>
    );
  };

  return (
    <ChartContainer
      config={chartConfig}
      className={cn('w-full', expansionState === 'open' && 'h-full')}
    >
      <BarChart
        accessibilityLayer
        data={chartData}
        layout="vertical"
        margin={{
          right: 30,
          top: 10,
        }}
      >
        <CartesianGrid horizontal={false} vertical={true} stroke="hsl(var(--muted-foreground))" />
        <YAxis
          type="category"
          dataKey="Project"
          width={110}
          yAxisId={0}
          className="fill-[--color-label]"
        />
        <YAxis
          type="category"
          dataKey="Project"
          width={110}
          yAxisId={1}
          className="fill-[--color-label]"
          hide={true}
        />
        <XAxis type="number" dataKey="Total" scale="linear" domain={[0, largest]} />
        <ChartTooltip
          cursor={false}
          content={<ChartTooltipContent indicator="line" labelValueGetter={getTooltipLabel} />}
        />
        {chartData?.map((bar, outerIndex) => {
          const projectData = bar[bar.Project];
          const sortedProjectTasks = sortProjectTasks(
            isProjectTaskData(projectData) ? projectData : {}
          );
          if (sortedProjectTasks.length === 0) {
            const projectColor =
              barColors[colorMap[`${bar.Project}`]] ?? uniqolor(bar.Project).color;
            return (
              <Bar
                key={`${bar.Project}-${outerIndex}`}
                dataKey={`${bar.Project}-Total`}
                layout="vertical"
                fill={projectColor}
                radius={4}
                yAxisId={0}
                stackId={0}
              >
                <LabelList
                  dataKey={`${bar.Project}-Total`}
                  position="center"
                  content={CustomLabel}
                  fill={calculateContrastRatio(projectColor, white) < 4.5 ? black : white}
                />
              </Bar>
            );
          } else {
            return sortedProjectTasks.map(([key, value]: [string, number], index) => {
              if (key !== 'Total' && key !== 'Project' && key !== `${bar.Project}-Total`) {
                const projectTaskColor =
                  barColors[colorMap[`${bar.Project}.${key}`]] ??
                  uniqolor(`${bar.Project}.${key}`).color;
                return (
                  <Bar
                    key={`${index} + ${outerIndex}`}
                    dataKey={`${bar.Project}.${key}`}
                    layout="vertical"
                    fill={projectTaskColor}
                    radius={4}
                    stackId={0}
                  >
                    {value !== 0 && (
                      <LabelList
                        dataKey={`${bar.Project}.${key}`}
                        position="center"
                        content={CustomLabel}
                        fill={calculateContrastRatio(projectTaskColor, white) < 4.5 ? black : white}
                      />
                    )}
                  </Bar>
                );
              }
            });
          }
        })}
      </BarChart>
    </ChartContainer>
  );
};

export const TimeHistoryChart = ({ filterData }: { filterData: TimeHistoryReportFormValues }) => {
  const [chartData, setChartData] = useState<TimeHistoryReportChartDataSource>();
  const { getFilteredTimesheetEntries, getTasksForSelectedProjects } = useReportData();

  const [expansionState, setExpansionState] = useState<AccordionExpansionState>('open');

  useEffect(() => {
    const timeSheet = getFilteredTimesheetEntries(filterData);
    setChartData(
      toTimeHistoryReportChartDataSource({
        timeSheet,
        getTasksForSelectedProjects,
      })
    );
  }, [filterData, getFilteredTimesheetEntries, getTasksForSelectedProjects]);

  return (
    <Card className="flex h-full flex-col rounded-md border bg-list">
      <CardHeader className="w-full pt-1">
        <CardTitle className="flex items-center justify-between">
          <span>Timesheet Hours</span>
          <Button
            variant="ghost"
            onClick={() => setExpansionState(expansionState === 'open' ? 'closed' : 'open')}
          >
            <ChevronDownIcon
              className={cn(
                'h-6 w-6 shrink-0 transition-transform duration-200',
                expansionState === 'open' && 'rotate-180'
              )}
            />
          </Button>
        </CardTitle>
        <CardDescription>
          {filterData.startDate && filterData.endDate
            ? `${filterData.startDate.toLocaleDateString()} - ${filterData.endDate.toLocaleDateString()}`
            : 'Select a date...'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden sm:pb-3">
        <ChartAccordionWrapper
          expansionState={expansionState}
          setExpansionState={setExpansionState}
        >
          <TimeHistoryBarChart
            chartData={chartData}
            filterData={filterData}
            expansionState={expansionState}
          />
        </ChartAccordionWrapper>
      </CardContent>
    </Card>
  );
};
